
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BMICalculator = () => {
  const [unit, setUnit] = useState<string>("metric");
  const [weight, setWeight] = useState<number>(70); // Default in kg
  const [heightCm, setHeightCm] = useState<number>(170); // Default in cm
  const [heightFeet, setHeightFeet] = useState<number>(5);
  const [heightInches, setHeightInches] = useState<number>(10);
  
  const [bmi, setBmi] = useState<number>(0);
  const [category, setCategory] = useState<string>("");
  const [healthyWeightRange, setHealthyWeightRange] = useState<{ min: number, max: number }>({ min: 0, max: 0 });

  useEffect(() => {
    calculateBMI();
  }, [weight, heightCm, heightFeet, heightInches, unit]);

  const calculateBMI = () => {
    let calculatedBMI = 0;
    let heightInMeters = 0;

    if (unit === "metric") {
      heightInMeters = heightCm / 100;
      calculatedBMI = weight / (heightInMeters * heightInMeters);
    } else {
      // Convert feet and inches to inches
      const totalInches = (heightFeet * 12) + heightInches;
      // Convert inches to meters (1 inch = 0.0254 meters)
      heightInMeters = totalInches * 0.0254;
      // Convert pounds to kg (1 pound = 0.453592 kg)
      const weightInKg = weight * 0.453592;
      calculatedBMI = weightInKg / (heightInMeters * heightInMeters);
    }

    setBmi(calculatedBMI);
    determineCategory(calculatedBMI);
    calculateHealthyWeightRange(heightInMeters);
  };

  const determineCategory = (bmiValue: number) => {
    if (bmiValue < 18.5) {
      setCategory("Underweight");
    } else if (bmiValue < 25) {
      setCategory("Normal weight");
    } else if (bmiValue < 30) {
      setCategory("Overweight");
    } else {
      setCategory("Obese");
    }
  };

  const calculateHealthyWeightRange = (heightInMeters: number) => {
    // Healthy BMI range is 18.5-24.9
    const minWeight = 18.5 * (heightInMeters * heightInMeters);
    const maxWeight = 24.9 * (heightInMeters * heightInMeters);

    if (unit === "metric") {
      setHealthyWeightRange({
        min: minWeight,
        max: maxWeight
      });
    } else {
      // Convert kg to pounds (1 kg = 2.20462 pounds)
      setHealthyWeightRange({
        min: minWeight * 2.20462,
        max: maxWeight * 2.20462
      });
    }
  };

  const getBMIColor = () => {
    if (bmi < 18.5) {
      return "text-blue-600";
    } else if (bmi < 25) {
      return "text-green-600";
    } else if (bmi < 30) {
      return "text-yellow-600";
    } else {
      return "text-red-600";
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Enter Your Details</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-medium text-gray-700">Unit System</Label>
                <RadioGroup
                  value={unit}
                  onValueChange={setUnit}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="metric" id="metric" />
                    <Label htmlFor="metric" className="cursor-pointer">Metric (kg, cm)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="imperial" id="imperial" />
                    <Label htmlFor="imperial" className="cursor-pointer">Imperial (lb, ft/in)</Label>
                  </div>
                </RadioGroup>
              </div>

              {unit === "metric" ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="weight" className="font-medium text-gray-700">Weight (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      min="20"
                      max="500"
                      step="0.1"
                      value={weight}
                      onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                      className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height" className="font-medium text-gray-700">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      min="50"
                      max="250"
                      step="1"
                      value={heightCm}
                      onChange={(e) => setHeightCm(parseFloat(e.target.value) || 0)}
                      className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="weight-lb" className="font-medium text-gray-700">Weight (lb)</Label>
                    <Input
                      id="weight-lb"
                      type="number"
                      min="50"
                      max="1000"
                      step="0.1"
                      value={weight}
                      onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                      className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="height-ft" className="font-medium text-gray-700">Height (ft)</Label>
                      <Select
                        value={heightFeet.toString()}
                        onValueChange={(value) => setHeightFeet(parseInt(value))}
                      >
                        <SelectTrigger id="height-ft" className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white">
                          <SelectValue placeholder="Feet" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 9 }, (_, i) => i + 1).map((feet) => (
                            <SelectItem key={feet} value={feet.toString()}>
                              {feet} ft
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="height-in" className="font-medium text-gray-700">Height (in)</Label>
                      <Select
                        value={heightInches.toString()}
                        onValueChange={(value) => setHeightInches(parseInt(value))}
                      >
                        <SelectTrigger id="height-in" className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white">
                          <SelectValue placeholder="Inches" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i).map((inches) => (
                            <SelectItem key={inches} value={inches.toString()}>
                              {inches} in
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Results</h2>
            
            <div className="text-center mb-6">
              <div className="text-4xl font-bold mb-2 leading-none">
                <span className={getBMIColor()}>{bmi.toFixed(1)}</span>
              </div>
              <div className="text-xl font-semibold">{category}</div>
            </div>
            
            <Table>
              <TableBody>
                <TableRow className="bg-[#E6F3FF]">
                  <TableCell className="font-medium text-gray-700">BMI</TableCell>
                  <TableCell className="text-right font-bold">{bmi.toFixed(1)}</TableCell>
                </TableRow>
                <TableRow className="bg-white">
                  <TableCell className="font-medium text-gray-700">Category</TableCell>
                  <TableCell className="text-right font-bold">{category}</TableCell>
                </TableRow>
                <TableRow className="bg-[#E6F3FF]">
                  <TableCell className="font-medium text-gray-700">Healthy Weight Range</TableCell>
                  <TableCell className="text-right font-bold">
                    {healthyWeightRange.min.toFixed(1)} - {healthyWeightRange.max.toFixed(1)} {unit === "metric" ? "kg" : "lb"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="mt-6 text-sm text-gray-600">
              <h3 className="font-semibold mb-2">BMI Categories:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Underweight: BMI less than 18.5</li>
                <li>Normal weight: BMI 18.5–24.9</li>
                <li>Overweight: BMI 25–29.9</li>
                <li>Obesity: BMI 30 or greater</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BMICalculator;
